import React, { useEffect, useState } from "react";
import {
    collection, addDoc, getDocs, updateDoc,
    deleteDoc, doc, query, orderBy, serverTimestamp
} from "firebase/firestore";
import { db } from "../../firebase";

import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, TablePagination, InputAdornment, Grid
} from "@mui/material";

import { Plus, Search, Edit3, Trash2 } from "lucide-react";
import Swal from "sweetalert2";

function Affiliatebank() {

    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [products, setProducts] = useState([]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [formData, setFormData] = useState({
        id: null,
        bankName: "",
        type: "",
        category: "",
        contactPerson: "",
        mobile: "",
        email: "",
        city: "",
        state: "",
        commissionType: "%",
        commissionValue: "",
        status: "Active",
        logo: "",
        productId: "",
        interestRate: "",
        tenure: "",
    });

    // 🔹 Fetch Data
    const fetchData = async () => {
        const q = query(collection(db, "affiliate_banks"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setData(arr);
    };

    useEffect(() => {
        fetchData();
        fetchProducts();

    }, []);

    const fetchProducts = async () => {
        const snap = await getDocs(collection(db, "affiliate_products"));
        const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setProducts(arr);
    };

    // 🔹 Save
    const handleSubmit = async () => {

        // Validation
        if (!formData.bankName.trim()) {
            return Swal.fire("Error", "Bank Name required", "error");
        }

        if (!formData.mobile || formData.mobile.length !== 10) {
            return Swal.fire("Error", "Valid mobile required", "error");
        }

        try {
            const payload = {
                bankName: formData.bankName,
                type: formData.type,
                productId: formData.productId,   // ✅ NEW
                interestRate: Number(formData.interestRate || 0), // ✅ NEW
                tenure: Number(formData.tenure || 0), // ✅ NEW
                category: formData.category,
                contactPerson: formData.contactPerson,
                mobile: formData.mobile,
                email: formData.email,
                city: formData.city,
                state: formData.state,
                commissionType: formData.commissionType,
                commissionValue: Number(formData.commissionValue || 0),
                status: formData.status,
                logo: formData.logo,
                updatedAt: serverTimestamp()
            };
            if (formData.id) {
                await updateDoc(doc(db, "affiliate_banks", formData.id), payload);
            } else {
                await addDoc(collection(db, "affiliate_banks"), {
                    ...payload,
                    createdAt: serverTimestamp()
                });
            }

            setOpen(false);
            resetForm();
            fetchData();

            Swal.fire("Success", "Saved successfully", "success");

        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    const handleDelete = async (id) => {
        try {
            const confirm = await Swal.fire({
                title: "Delete?",
                text: "This action cannot be undone",
                icon: "warning",
                showCancelButton: true
            });

            if (confirm.isConfirmed) {
                await deleteDoc(doc(db, "affiliate_banks", id));

                Swal.fire("Deleted!", "Record removed", "success");
                fetchData();
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };


    const resetForm = () => {
        setFormData({
            id: null,
            bankName: "",
            type: "",
            category: "",
            contactPerson: "",
            mobile: "",
            email: "",
            city: "",
            state: "",
            commissionType: "%",
            commissionValue: "",
            status: "Active",
            logo: "",
        });
    };

    // 🔹 Search
    const filtered = data.filter(d =>
        d.bankName?.toLowerCase().includes(search.toLowerCase()) ||
        d.mobile?.includes(search)
    );

    const getProductName = (id) => {
        const p = products.find(x => x.id === id);
        return p ? p.productName : "-";
    };

    return (
        <Box p={2}>

            {/* HEADER */}
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Affiliate Banks</Typography>
                <Button variant="contained" size="small" startIcon={<Plus />}
                    onClick={() => { resetForm(); setOpen(true); }}>
                    Add Bank
                </Button>
            </Box>

            {/* SEARCH */}
            <TextField
                fullWidth
                size="small"
                placeholder="Search bank / mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search size={16} />
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 2 }}
            />

            {/* TABLE */}
            <Paper>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>Interest</TableCell>
                                <TableCell>Tenure</TableCell>
                                <TableCell>Logo</TableCell>
                                <TableCell>Bank</TableCell>

                                <TableCell>Type</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Mobile</TableCell>
                                <TableCell>Commission</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filtered
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, i) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                                        <TableCell>{getProductName(row.productId)}</TableCell>
                                        <TableCell>{row.interestRate}%</TableCell>
                                        <TableCell>{row.tenure} M</TableCell>
                                        <TableCell>
                                            <img src={row.logo} height={50} width={50} />
                                        </TableCell>
                                        <TableCell>{row.bankName}</TableCell>

                                        <TableCell>{row.type}</TableCell>
                                        <TableCell>{row.contactPerson}</TableCell>
                                        <TableCell>{row.mobile}</TableCell>
                                        <TableCell>{row.commissionValue}{row.commissionType}</TableCell>
                                        <TableCell>{row.status}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => {
                                                setFormData({
                                                    id: row.id,
                                                    bankName: row.bankName || "",
                                                    type: row.type || "",
                                                    productId: row.productId || "",   // ✅ NEW
                                                    interestRate: row.interestRate || "", // ✅ NEW
                                                    tenure: row.tenure || "", // ✅ NEW
                                                    category: row.category || "",
                                                    contactPerson: row.contactPerson || "",
                                                    mobile: row.mobile || "",
                                                    email: row.email || "",
                                                    city: row.city || "",
                                                    state: row.state || "",
                                                    commissionType: row.commissionType || "%",
                                                    commissionValue: row.commissionValue || "",
                                                    status: row.status || "Active",
                                                    logo: row.logo || ""
                                                });
                                                setOpen(true);
                                            }}>
                                                <Edit3 size={16} />
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(row.id)}>
                                                <Trash2 size={16} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    count={filtered.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* MODAL */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{formData.id ? "Edit Bank" : "Add Bank"}</DialogTitle>

                <DialogContent>
                    <Grid className="row" spacing={2} mt={1}>
                        {/* Product */}
                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField
                                select
                                label="Product"
                                size="small"
                                fullWidth
                                value={formData.productId}
                                onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                                SelectProps={{ native: true }}
                            >
                                <option value="">Select Product</option>
                                {products.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.productName}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Bank Name" size="small" fullWidth
                                value={formData.bankName}
                                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                            />
                        </Grid>



                        {/* Interest */}
                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField
                                label="Interest Rate (%)"
                                size="small"
                                fullWidth
                                value={formData.interestRate}
                                onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                            />
                        </Grid>

                        {/* Tenure */}
                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField
                                label="Tenure (Months)"
                                size="small"
                                fullWidth
                                value={formData.tenure}
                                onChange={(e) => setFormData({ ...formData, tenure: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Logo Link" size="small" fullWidth
                                value={formData.logo}
                                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Type (Bank/NBFC)" size="small" fullWidth
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Category" size="small" fullWidth
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Contact Person" size="small" fullWidth
                                value={formData.contactPerson}
                                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Mobile" size="small" fullWidth
                                value={formData.mobile}
                                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Email" size="small" fullWidth
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="City" size="small" fullWidth
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="State" size="small" fullWidth
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField label="Commission (%) / ₹" size="small" fullWidth
                                value={formData.commissionValue}
                                onChange={(e) => setFormData({ ...formData, commissionValue: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField select size="small" label="Status" fullWidth
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                SelectProps={{ native: true }}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </TextField>
                        </Grid>

                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Affiliatebank;